import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Target, TrendingUp, Crown, Users as UsersIcon } from 'lucide-react';
import HistorialMetasPanel from './HistorialMetasPanel';
import MetasCerradasSection from './MetasCerradasSection';
import MetasChatterCerradasSection from './MetasChatterCerradasSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoalCircle = ({ progress, color, size = 140 }) => {
  const r = (size / 2) - 10;
  const circ = 2 * Math.PI * r;
  const cx = size / 2;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#27272a" strokeWidth="8" />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${(Math.min(progress, 100) / 100) * circ} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`font-bold text-white ${size >= 120 ? 'text-3xl' : 'text-xl'}`}>{Math.round(progress)}%</div>
        {size >= 120 && <div className="text-[10px] text-gray-400">completado</div>}
      </div>
    </div>
  );
};

const colorFor = (progress) =>
  progress >= 100
    ? '#22c55e'
    : progress >= 75
    ? '#3b82f6'
    : progress >= 50
    ? '#a855f7'
    : '#f97316';

export default function MetasPanel({ readOnly = false, hideEmployeeGoals = false, chatterMode = false }) {
  const [employeeGoals, setEmployeeGoals] = useState([]);
  const [modelGoals, setModelGoals] = useState([]);
  const [models, setModels] = useState([]);
  const [users, setUsers] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dialog para ver desglose de reportes de una meta
  const [reportsDialog, setReportsDialog] = useState({ open: false, data: null, loading: false });

  // Employee goal dialog
  const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false);
  const [newEmployeeGoal, setNewEmployeeGoal] = useState({
    user_id: '',
    goal_amount: 0,
    fecha_inicio: '',
    fecha_fin: '',
    period_name: '',
  });

  // Model goal dialog
  const [openModelDialog, setOpenModelDialog] = useState(false);
  const [editingModelGoal, setEditingModelGoal] = useState(null);
  const [newModelGoal, setNewModelGoal] = useState({
    modelo_id: '',
    goal_amount: 0,
    fecha_inicio: '',
    fecha_fin: '',
    period_name: '',
    notas: '',
  });

  const fetchAll = async () => {
    setLoading(true);
    
    // Modelos (crítico) con fallback
    try {
      const mRes = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(Array.isArray(mRes.data) ? mRes.data : []);
    } catch (err) {
      console.error('[Metas] /api/models falló:', err?.response?.status);
      try {
        const fb = await axios.get(`${API}/models/list`, { withCredentials: true });
        setModels(Array.isArray(fb.data) ? fb.data : []);
      } catch (err2) {
        console.error('[Metas] /api/models/list también falló:', err2?.response?.status);
      }
    }
    
    // Goals empleados (solo si admin)
    if (!hideEmployeeGoals) {
      try {
        const egRes = await axios.get(`${API}/goals`, { withCredentials: true });
        setEmployeeGoals(Array.isArray(egRes.data) ? egRes.data : []);
      } catch (err) {
        console.error('[Metas] /api/goals:', err?.response?.status);
        setEmployeeGoals([]);
      }
    }
    
    // Goals modelos
    try {
      const mgRes = await axios.get(`${API}/model-goals`, { withCredentials: true });
      setModelGoals(Array.isArray(mgRes.data) ? mgRes.data : []);
    } catch (err) {
      console.error('[Metas] /api/model-goals:', err?.response?.status);
      setModelGoals([]);
    }
    
    // Usuarios (solo si admin)
    if (!hideEmployeeGoals) {
      try {
        const uRes = await axios.get(`${API}/users`, { withCredentials: true });
        setUsers(Array.isArray(uRes.data) ? uRes.data : []);
      } catch (err) {
        console.error('[Metas] /api/users:', err?.response?.status);
        setUsers([]);
      }
    }
    
    // Facturación por modelo (acumulado desde inicio del año)
    // En modo chatter no llamamos este endpoint (no tienen permisos y no necesitan ver montos)
    if (!chatterMode) {
      try {
        const yearStart = `${new Date().getFullYear()}-01-01`;
        const today = new Date().toISOString().split('T')[0];
        const bRes = await axios.get(`${API}/reports/billing-by-model?fecha_inicio=${yearStart}&fecha_fin=${today}`, { withCredentials: true });
        setBilling(bRes.data);
      } catch (err) {
        console.error('[Metas] /api/reports/billing-by-model:', err?.response?.status);
        setBilling(null);
      }
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleCreateEmployeeGoal = async () => {
    if (!newEmployeeGoal.user_id || !newEmployeeGoal.goal_amount || !newEmployeeGoal.fecha_inicio || !newEmployeeGoal.fecha_fin) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    try {
      await axios.post(`${API}/goals`, newEmployeeGoal, { withCredentials: true });
      toast.success('Meta de empleado creada');
      setOpenEmployeeDialog(false);
      setNewEmployeeGoal({ user_id: '', goal_amount: 0, fecha_inicio: '', fecha_fin: '', period_name: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear meta');
    }
  };

  const handleDeleteEmployeeGoal = async (id) => {
    if (!window.confirm('¿Eliminar esta meta?')) return;
    try {
      await axios.delete(`${API}/goals/${id}`, { withCredentials: true });
      toast.success('Meta eliminada');
      fetchAll();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveModelGoal = async () => {
    if (!newModelGoal.modelo_id || !newModelGoal.goal_amount || !newModelGoal.fecha_inicio || !newModelGoal.fecha_fin) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    try {
      if (editingModelGoal) {
        await axios.put(`${API}/model-goals/${editingModelGoal.id}`, newModelGoal, { withCredentials: true });
        toast.success('Meta de modelo actualizada');
      } else {
        await axios.post(`${API}/model-goals`, newModelGoal, { withCredentials: true });
        toast.success('Meta de modelo creada');
      }
      setOpenModelDialog(false);
      setEditingModelGoal(null);
      setNewModelGoal({ modelo_id: '', goal_amount: 0, fecha_inicio: '', fecha_fin: '', period_name: '', notas: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar meta');
    }
  };

  const handleDeleteModelGoal = async (id) => {
    if (!window.confirm('¿Eliminar esta meta de modelo?')) return;
    try {
      await axios.delete(`${API}/model-goals/${id}`, { withCredentials: true });
      toast.success('Meta eliminada');
      fetchAll();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const startEditModelGoal = (goal) => {
    setEditingModelGoal(goal);
    setNewModelGoal({
      modelo_id: goal.modelo_id,
      goal_amount: goal.goal_amount,
      fecha_inicio: goal.fecha_inicio,
      fecha_fin: goal.fecha_fin,
      period_name: goal.period_name || '',
      notas: goal.notas || '',
    });
    setOpenModelDialog(true);
  };

  const handleViewGoalReports = async (goal) => {
    setReportsDialog({ open: true, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/model-goals/${goal.id}/reports`, { withCredentials: true });
      setReportsDialog({ open: true, data: res.data, loading: false });
    } catch (err) {
      console.error('[Metas] reportes:', err?.response?.status);
      toast.error('Error al cargar reportes de la meta');
      setReportsDialog({ open: false, data: null, loading: false });
    }
  };

  const handleViewBillingReports = async (modelRow, fechaInicio, fechaFin) => {
    // Reusa el mismo dialog de reportes — la API /reports/by-model devuelve
    // estructura compatible. Adaptamos al shape que espera el dialog.
    setReportsDialog({ open: true, data: null, loading: true });
    try {
      const params = new URLSearchParams({ fecha_inicio: fechaInicio || '', fecha_fin: fechaFin || '' });
      const res = await axios.get(`${API}/reports/by-model/${modelRow.modelo_id}?${params.toString()}`, { withCredentials: true });
      const data = res.data || {};
      setReportsDialog({
        open: true,
        loading: false,
        data: {
          modelo_name: data.modelo_name,
          fecha_inicio: data.fecha_inicio,
          fecha_fin: data.fecha_fin,
          total_acumulado: data.total_acumulado || 0,
          goal_amount: data.total_acumulado || 1, // sin meta — usamos total para barra al 100%
          reports_count: data.reports_count || 0,
          reports: data.reports || [],
          _is_billing: true,
        },
      });
    } catch (err) {
      console.error('[Metas] billing reportes:', err?.response?.status);
      toast.error('No se pudieron cargar los reportes');
      setReportsDialog({ open: false, data: null, loading: false });
    }
  };

  if (loading) {
    return <div className="text-gray-400 text-center py-12">Cargando metas...</div>;
  }

  // Eligible users for employee goals (chatters / admin / asistente)
  const eligibleUsers = users.filter((u) =>
    ['chatter', 'administrativo', 'asistente'].includes(u.role)
  );

  return (
    <div className="space-y-8">
      {/* ================ FACTURACIÓN POR MODELO ================ */}
      {!chatterMode && (
      <Card className="bg-[#0a0a0a] border border-emerald-500/20 rounded-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-white text-xl">Facturación por Modelo</CardTitle>
              <p className="text-xs text-gray-400 mt-0.5">
                Solo cuenta lo que los chatters reportaron explícitamente en cada modelo
                {billing && ` · ${billing.fecha_inicio} a ${billing.fecha_fin}`}
              </p>
            </div>
            {billing && (
              <div className="text-right">
                <div className="text-xs text-gray-400">Total</div>
                <div className="text-2xl font-bold text-emerald-400">
                  ${billing.grand_total.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!billing || billing.by_model.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No hay facturación reportada todavía. Cuando los chatters asignen ventas a una modelo en sus reportes, aparecerán aquí.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto pr-1">
              {billing.by_model.map((m) => (
                <div
                  key={m.modelo_id}
                  onClick={() => !readOnly && handleViewBillingReports(m, billing.fecha_inicio, billing.fecha_fin)}
                  className={`flex items-center gap-3 bg-zinc-800/50 border border-white/5 rounded-lg p-3 transition-all ${
                    !readOnly ? 'cursor-pointer hover:border-emerald-500/50 hover:bg-emerald-500/5' : ''
                  }`}
                  data-testid={`billing-row-${m.modelo_id}`}
                >
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.modelo_name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-zinc-700 flex items-center justify-center text-xl shrink-0">👤</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{m.modelo_name}</div>
                    <div className="text-emerald-400 font-bold text-lg">${m.total.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500">{m.report_count} reportes</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ================ METAS DE EMPLEADOS ================ */}
      {!hideEmployeeGoals && (
      <Card
        data-testid="metas-empleados-card"
        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl"
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <UsersIcon className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-white text-xl">Metas Individuales · Empleados</CardTitle>
          </div>
          <Dialog open={openEmployeeDialog} onOpenChange={setOpenEmployeeDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid="assign-employee-goal-btn"
                className="bg-purple-600 hover:bg-purple-700"
              >
                + Asignar Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">Asignar Meta a Empleado</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Empleado</Label>
                  <Select
                    value={newEmployeeGoal.user_id}
                    onValueChange={(v) => setNewEmployeeGoal({ ...newEmployeeGoal, user_id: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Seleccionar empleado" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {eligibleUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id} className="text-white">
                          {u.name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Meta ($)</Label>
                  <Input
                    type="number"
                    value={newEmployeeGoal.goal_amount}
                    onChange={(e) =>
                      setNewEmployeeGoal({
                        ...newEmployeeGoal,
                        goal_amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">Fecha inicio</Label>
                    <Input
                      type="date"
                      value={newEmployeeGoal.fecha_inicio}
                      onChange={(e) =>
                        setNewEmployeeGoal({ ...newEmployeeGoal, fecha_inicio: e.target.value })
                      }
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Fecha fin</Label>
                    <Input
                      type="date"
                      value={newEmployeeGoal.fecha_fin}
                      onChange={(e) =>
                        setNewEmployeeGoal({ ...newEmployeeGoal, fecha_fin: e.target.value })
                      }
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Nombre del periodo (opcional)</Label>
                  <Input
                    value={newEmployeeGoal.period_name}
                    onChange={(e) =>
                      setNewEmployeeGoal({ ...newEmployeeGoal, period_name: e.target.value })
                    }
                    placeholder="Ej: Quincena 1 de Enero"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <Button
                  onClick={handleCreateEmployeeGoal}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Guardar Meta
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {employeeGoals.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No hay metas asignadas. ¡Asigna la primera!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {employeeGoals.map((goal) => {
                const progress = (goal.current_amount / goal.goal_amount) * 100 || 0;
                const color = colorFor(progress);
                return (
                  <Card
                    key={goal.id}
                    className="bg-zinc-800 border-2 border-zinc-700 hover:border-purple-500 transition-all"
                    data-testid={`employee-goal-card-${goal.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="text-center mb-4 pb-4 border-b border-zinc-700 relative">
                        <button
                          onClick={() => handleDeleteEmployeeGoal(goal.id)}
                          className="absolute top-0 right-0 text-red-500 hover:text-red-400 p-1"
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                        <div className="font-bold text-white text-xl mb-1">{goal.user_name}</div>
                        <div className="text-xs text-gray-400">
                          {goal.period_name || `${goal.fecha_inicio} → ${goal.fecha_fin}`}
                        </div>
                      </div>
                      <div className="flex justify-center items-center mb-6" style={{ minHeight: 180 }}>
                        <GoalCircle progress={progress} color={color} />
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Meta Asignada</div>
                          <div className="text-2xl font-bold text-white">
                            ${goal.goal_amount.toLocaleString()}
                          </div>
                        </div>
                        <div className="border-t border-zinc-700 pt-3">
                          <div className="text-xs text-gray-500 mb-1">Progreso Actual</div>
                          <div className="text-xl font-bold text-green-400">
                            ${(goal.current_amount || 0).toFixed(0)}
                          </div>
                          {goal.current_hours > 0 && (
                            <div className="text-sm text-blue-400 mt-1">
                              ⏱️ {goal.current_hours.toFixed(1)}h trabajadas
                            </div>
                          )}
                        </div>
                      </div>
                      {progress >= 100 && (
                        <div className="mt-4 p-2 bg-green-500/20 rounded-lg text-center">
                          <span className="text-green-400 text-xs font-semibold">
                            🏆 ¡Meta Alcanzada!
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ================ METAS POR MODELO ================ */}
      <Card
        data-testid="metas-modelos-card"
        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl"
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-white text-xl">Metas Individuales · Modelos</CardTitle>
          </div>
          {!readOnly && (
          <Dialog
            open={openModelDialog}
            onOpenChange={(open) => {
              setOpenModelDialog(open);
              if (!open) {
                setEditingModelGoal(null);
                setNewModelGoal({ modelo_id: '', goal_amount: 0, fecha_inicio: '', fecha_fin: '', period_name: '', notas: '' });
              }
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="assign-model-goal-btn" className="bg-pink-600 hover:bg-pink-700">
                + Asignar Meta a Modelo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingModelGoal ? 'Editar Meta de Modelo' : 'Asignar Meta a Modelo'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Modelo</Label>
                  <Select
                    value={newModelGoal.modelo_id}
                    onValueChange={(v) => setNewModelGoal({ ...newModelGoal, modelo_id: v })}
                    disabled={!!editingModelGoal}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Seleccionar modelo" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {models.filter(m => m.status !== 'inactiva').map((m) => (
                        <SelectItem key={m.id} value={m.id} className="text-white">
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Meta de Ingreso ($)</Label>
                  <Input
                    type="number"
                    value={newModelGoal.goal_amount}
                    onChange={(e) =>
                      setNewModelGoal({
                        ...newModelGoal,
                        goal_amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">Fecha inicio</Label>
                    <Input
                      type="date"
                      value={newModelGoal.fecha_inicio}
                      onChange={(e) =>
                        setNewModelGoal({ ...newModelGoal, fecha_inicio: e.target.value })
                      }
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Fecha fin</Label>
                    <Input
                      type="date"
                      value={newModelGoal.fecha_fin}
                      onChange={(e) =>
                        setNewModelGoal({ ...newModelGoal, fecha_fin: e.target.value })
                      }
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Nombre del periodo (opcional)</Label>
                  <Input
                    value={newModelGoal.period_name}
                    onChange={(e) =>
                      setNewModelGoal({ ...newModelGoal, period_name: e.target.value })
                    }
                    placeholder="Ej: Enero 2026"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Notas (opcional)</Label>
                  <Input
                    value={newModelGoal.notas}
                    onChange={(e) => setNewModelGoal({ ...newModelGoal, notas: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 italic">
                  💡 El progreso se calcula automáticamente sumando los ingresos generados por los
                  grupos que contienen a esta modelo durante el periodo.
                </p>
                <Button
                  onClick={handleSaveModelGoal}
                  className="w-full bg-pink-600 hover:bg-pink-700"
                >
                  {editingModelGoal ? 'Guardar Cambios' : 'Crear Meta'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {modelGoals.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {readOnly ? 'No hay metas asignadas a modelos por el momento.' : 'No hay metas asignadas a modelos. ¡Asigna la primera!'}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {modelGoals.map((goal) => {
                // En modo chatter el backend manda progress_pct y oculta los montos
                const progress = chatterMode
                  ? Number(goal.progress_pct || 0)
                  : (goal.current_amount / goal.goal_amount) * 100 || 0;
                const color = colorFor(progress);
                return (
                  <Card
                    key={goal.id}
                    onClick={() => !readOnly && handleViewGoalReports(goal)}
                    className={`bg-zinc-800 border border-zinc-700 hover:border-pink-500 transition-all ${!readOnly ? 'cursor-pointer' : ''}`}
                    data-testid={`model-goal-card-${goal.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="text-center mb-2 pb-2 border-b border-zinc-700 relative">
                        {!readOnly && (
                        <div className="absolute top-0 right-0 flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); startEditModelGoal(goal); }}
                            className="text-blue-500 hover:text-blue-400 p-0.5 text-xs"
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteModelGoal(goal.id); }}
                            className="text-red-500 hover:text-red-400 p-0.5 text-xs"
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                        )}
                        <div className="font-semibold text-white text-sm truncate mb-0.5">{goal.modelo_name}</div>
                        <div className="text-[10px] text-gray-500">
                          {goal.period_name || `${goal.fecha_inicio} → ${goal.fecha_fin}`}
                        </div>
                      </div>
                      <div className="flex justify-center items-center mb-3">
                        <GoalCircle progress={progress} color={color} size={100} />
                      </div>
                      {!chatterMode && (
                        <div className="space-y-1.5">
                          <div className="flex items-baseline justify-between">
                            <span className="text-[10px] text-gray-500 uppercase">Meta</span>
                            <span className="text-sm font-semibold text-white">
                              ${goal.goal_amount.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-[10px] text-gray-500 uppercase">Logrado</span>
                            <span className="text-sm font-bold text-emerald-400">
                              ${(goal.current_amount || 0).toFixed(0)}
                            </span>
                          </div>
                          {goal.notas && (
                            <div className="pt-1 border-t border-zinc-800 text-[10px] text-gray-400 truncate" title={goal.notas}>
                              {goal.notas}
                            </div>
                          )}
                        </div>
                      )}
                      {progress >= 100 && (
                        <div className="mt-2 py-1 bg-green-500/15 rounded text-center">
                          <span className="text-green-400 text-[10px] font-semibold">
                            🏆 Alcanzada
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ METAS CERRADAS (HISTORIAL) ============ */}
      {!chatterMode && (
        <MetasCerradasSection onSelectGoal={(g) => handleViewGoalReports(g)} />
      )}
      {!chatterMode && <MetasChatterCerradasSection />}

      {/* ============ HISTORIAL DE RENDIMIENTO DE CHATTERS ============ */}
      {!chatterMode && <HistorialMetasPanel />}

      {/* ============ DIALOG: DESGLOSE DE REPORTES DE UNA META ============ */}
      <Dialog
        open={reportsDialog.open}
        onOpenChange={(open) => setReportsDialog({ ...reportsDialog, open })}
      >
        <DialogContent className="w-[95vw] max-w-2xl bg-zinc-900 border-zinc-700 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              📊 Desglose de Reportes
            </DialogTitle>
          </DialogHeader>
          {reportsDialog.loading ? (
            <div className="text-center py-8 text-gray-400">Cargando reportes...</div>
          ) : reportsDialog.data ? (
            <div className="space-y-4">
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs text-gray-400">Modelo</div>
                    <div className="text-white font-bold text-lg">{reportsDialog.data.modelo_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Acumulado / Meta</div>
                    <div className="text-emerald-400 font-bold text-xl">
                      ${reportsDialog.data.total_acumulado.toLocaleString()} / ${reportsDialog.data.goal_amount.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  Periodo: {reportsDialog.data.fecha_inicio} → {reportsDialog.data.fecha_fin} · {reportsDialog.data.reports_count} reportes
                </div>
              </div>
              
              {reportsDialog.data.reports.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Aún no hay reportes con ventas asignadas a esta modelo en el periodo.
                </div>
              ) : (
                <div className="space-y-2">
                  {reportsDialog.data.reports.map((r) => (
                    <div
                      key={r.report_id}
                      className="bg-zinc-800 border border-white/5 rounded-lg p-3"
                      data-testid={`report-detail-${r.report_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold">{r.user_name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">Chatter</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">📅 {r.date} · ⏱️ {r.hours_worked}h</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-gray-500">Asignado a esta modelo</div>
                          <div className="text-emerald-400 font-bold text-lg">${r.amount_for_model}</div>
                        </div>
                      </div>
                      {(r.relevant_sales || r.notes) && (
                        <div className="border-t border-white/5 mt-2 pt-2 text-xs text-gray-400 space-y-1">
                          {r.relevant_sales && <div><span className="text-pink-300">Ventas relevantes:</span> {r.relevant_sales}</div>}
                          {r.notes && <div><span className="text-cyan-300">Notas:</span> {r.notes}</div>}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-600 mt-2">
                        Reporte total: ${r.total_money_collected.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
